import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoIcon from '@mui/icons-material/Info';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';
import classNames from 'classnames';
import Checkbox from '@mui/material/Checkbox';
import * as React from 'react';
import { memo, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()((theme) => ({
  column: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 262,
  },
  noOffset: {
    marginLeft: 0,
    height: 28,
  },
  excludeText: {
    color: theme.palette.text.secondary,
  },
  imgClass: {
    marginLeft: 8,
    height: 24,
    width: 24,
  },
  unChecked: {
    display: 'flex',
    alignItems: 'center',
    userSelect: 'none',
  },
  checkedColor: {
    color: theme.palette.primary.main,
  },
  subText: {
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(4),
    userSelect: 'none',
  },
  displayGrid: {
    display: 'grid',
  },
  perSplitBannerOuter: {
    width: '100%',
  },
  perSplitBanner: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.info.light,
    width: '100%',
  },
  perSplitBannerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  perSplitInfoIcon: {
    color: theme.palette.info.dark,
    fontSize: 20,
    flexShrink: 0,
  },
  perSplitBannerMessage: {
    color: theme.palette.info.dark,
  },
  editSplitsAction: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    flexShrink: 0,
  },
  editSplitsLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    color: theme.palette.primary.main,
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    border: 'none',
    background: 'none',
    padding: 0,
    fontSize: theme.typography.captionRegular.fontSize,
    fontFamily: theme.typography.captionRegular.fontFamily,
    fontWeight: theme.typography.captionRegular.fontWeight,
    lineHeight: theme.typography.captionRegular.lineHeight,
    letterSpacing: theme.typography.captionRegular.letterSpacing,
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
}));

function VisibilitySection({
  excludeReports,
  excludeF2S,
  setExcludeReports,
  setExcludeF2S,
  className,
  leftAlign = false,
  disabledReports,
  indeterminateF2S,
  indeterminateReports,
  hideTitle,
  managedPerSplit,
  onEditSplits,
  editSplitsDisabled,
}: {
  excludeReports?: boolean | null;
  excludeF2S?: boolean | null;
  setExcludeReports: (exclude: boolean) => void;
  setExcludeF2S: (exclude: boolean) => void;
  className?: string;
  leftAlign?: boolean;
  disabledReports?: boolean;
  indeterminateF2S?: boolean;
  indeterminateReports?: boolean;
  hideTitle?: boolean;
  /** When true, exclusions are read-only; show per-split info and optional Edit Splits entry point. */
  managedPerSplit?: boolean;
  onEditSplits?: () => void;
  editSplitsDisabled?: boolean;
}) {
  const { classes, cx } = useStyles();
  const theme = useTheme();

  const onReportsClick = () => {
    setExcludeReports(!excludeReports);
  };
  const onF2SClick = () => {
    setExcludeF2S(!excludeF2S);
  };

  const vars = [
    {
      name: 'Spending Plan',
      image: <ExcludeFromSpendingPlan className={classes.imgClass} />,
      checked: excludeF2S,
      onChange: onF2SClick,
      id: 'ignore-spending-plan-toggle',
      subText: 'Affects Spending Plan calculations.',
      disabled: Boolean(managedPerSplit),
      indeterminate: indeterminateF2S,
    },
    {
      name: 'Reports',
      image: <ExcludeFromReports className={classes.imgClass} />,
      checked: disabledReports || excludeReports,
      onChange: onReportsClick,
      id: 'ignore-reports-toggle',
      subText: 'Affects Reports, Watchlists, and Upcoming Summary.',
      disabled: Boolean(managedPerSplit) || Boolean(disabledReports),
      indeterminate: indeterminateReports,
    },
  ];

  const noOffset = useMemo(() => ({ root: classes.noOffset }), [classes.noOffset]);
  const disabledUncheckedIcon = useMemo(
    () => (
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: 1,
          border: `1px solid ${theme.palette.text.disabled}`,
          boxSizing: 'border-box',
        }}
      />
    ),
    [theme.palette.text.disabled],
  );
  const disabledCheckedIcon = useMemo(
    () => (
      <Box
        sx={{
          boxSizing: 'border-box',
          border: `1px solid ${theme.palette.text.disabled}`,
          borderRadius: 1,
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.text.disabled,
        }}
      >
        <CheckRoundedIcon sx={{ fontSize: 18, color: theme.palette.common.white }} />
      </Box>
    ),
    [theme.palette.common.white, theme.palette.text.disabled],
  );
  const disabledIndeterminateIcon = useMemo(
    () => (
      <Box
        sx={{
          boxSizing: 'border-box',
          border: `1px solid ${theme.palette.text.disabled}`,
          borderRadius: 1,
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.text.disabled,
        }}
      >
        <Box
          sx={{
            width: 10,
            height: 2,
            borderRadius: 1,
            backgroundColor: theme.palette.common.white,
          }}
        />
      </Box>
    ),
    [theme.palette.common.white, theme.palette.text.disabled],
  );

  return (
    <div id='visibility-section' className={cx(classes.column, className)}>
      {!hideTitle && (
        <Typography variant='paragraphRegular' className={classes.excludeText}>
          Exclude From:
        </Typography>
      )}
      {managedPerSplit ? (
        <Box className={classes.perSplitBannerOuter}>
          <Box className={classes.perSplitBanner}>
            <Box className={classes.perSplitBannerLeft}>
              <InfoIcon className={classes.perSplitInfoIcon} aria-hidden />
              <Typography variant='captionRegular' component='span' className={classes.perSplitBannerMessage}>
                This setting is managed per split.
              </Typography>
            </Box>
            {onEditSplits ? (
              <Box className={classes.editSplitsAction}>
                {editSplitsDisabled ? (
                  <Typography
                    variant='captionRegular'
                    color='text.disabled'
                    component='span'
                    className={classes.editSplitsAction}
                  >
                    Edit Splits
                    <ChevronRightIcon sx={{ fontSize: 16 }} aria-hidden />
                  </Typography>
                ) : (
                  <button
                    type='button'
                    className={classes.editSplitsLink}
                    onClick={onEditSplits}
                    id='exclude-from-edit-splits'
                  >
                    Edit Splits
                    <ChevronRightIcon sx={{ fontSize: 16, color: 'inherit' }} aria-hidden />
                  </button>
                )}
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}
      {vars.map((item) => (
        <div className={classes.displayGrid} key={item.id}>
          <FormControlLabel
            disabled={item.disabled}
            control={
              <Checkbox
                id={`q-checkbox-${item.id}`}
                icon={item.disabled ? disabledUncheckedIcon : undefined}
                checkedIcon={item.disabled ? disabledCheckedIcon : undefined}
                indeterminateIcon={item.disabled ? disabledIndeterminateIcon : undefined}
                sx={(theme) => ({
                  ml: -1,
                  '&.Mui-disabled': {
                    color: `${theme.palette.text.disabled} !important`,
                    opacity: 1,
                  },
                  '&.Mui-checked.Mui-disabled, &.MuiCheckbox-indeterminate.Mui-disabled': {
                    color: `${theme.palette.text.disabled} !important`,
                  },
                })} // needed to overcome internal padding for ripple
                indeterminate={item.indeterminate && item.checked === null}
              />
            }
            label={
              <div className={classNames(classes.unChecked, item.checked && !item.disabled && classes.checkedColor)}>
                <Typography>{item.name}</Typography>
                {item.image}
              </div>
            }
            labelPlacement='end'
            onChange={item.onChange}
            checked={item.checked ?? false}
            classes={leftAlign ? undefined : noOffset}
            id={item.id}
          />
          <Typography
            variant='captionRegular'
            className={classes.subText}
            sx={(theme) => ({
              color: item.disabled
                ? theme.palette.text.disabled
                : item.checked
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
            })}
          >
            {item.subText}
          </Typography>
        </div>
      ))}
    </div>
  );
}

export default memo(VisibilitySection);
